// Category management
import { 
  API_BASE, allCategories, userCategories, currentCategory, categoryIcons,
  DEFAULT_CATEGORIES, ADDITIONAL_CATEGORIES, isSignedIn,
  setAllCategories, setUserCategories, setCurrentCategory
} from './state.js';
import { showToast } from './utils.js';
import { loadStories } from './stories.js';

export async function loadCategories() {
  try {
    const response = await fetch(`${API_BASE}/categories`);
    setAllCategories(await response.json());
    renderCategoryTabs();
  } catch (error) {
    console.error('Failed to load categories:', error);
  }
}

export function renderCategoryTabs() {
  const categoryTabs = document.getElementById('category-tabs');
  const visibleCategories = allCategories.filter(cat => userCategories.includes(cat.id));

  categoryTabs.innerHTML = visibleCategories.map(cat => `
    <button class="category-tab ${cat.id === currentCategory ? 'active' : ''}" data-category="${cat.id}">
      <span class="category-icon">${categoryIcons[cat.id] || '📰'}</span>
      ${cat.name}
    </button>
  `).join('') + `
    <button class="category-tab add-category-btn" id="add-category-btn" title="Add more topics">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
    </button>
  `;

  categoryTabs.querySelectorAll('.category-tab:not(.add-category-btn)').forEach(tab => {
    tab.addEventListener('click', () => {
      const category = tab.dataset.category;
      if (category !== currentCategory) {
        setCurrentCategory(category);
        document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        loadStories(category);
      }
    });
  });

  const addBtn = document.getElementById('add-category-btn');
  if (addBtn) addBtn.addEventListener('click', openCategoryPicker);
}

export function openCategoryPicker() {
  if (!isSignedIn) {
    const signinBody = document.querySelector('#signin-prompt-modal .modal-body h3');
    if (signinBody) signinBody.textContent = 'Sign in to customize topics';
    const signinDesc = document.querySelector('#signin-prompt-modal .modal-body p');
    if (signinDesc) signinDesc.textContent = 'Sign in with Google to add more news topics and save your preferences across devices.';
    document.getElementById('signin-prompt-modal').classList.add('active');
    return;
  }
  renderCategoryPicker();
  document.getElementById('category-picker-modal').classList.add('active');
}

export function closeCategoryPicker() {
  document.getElementById('category-picker-modal').classList.remove('active');
}

function renderCategoryPicker() {
  const pickerOptions = document.getElementById('category-picker-options');
  if (!pickerOptions) return;

  const availableToAdd = allCategories.filter(cat => 
    ADDITIONAL_CATEGORIES.includes(cat.id) && !userCategories.includes(cat.id));
  const addedCategories = allCategories.filter(cat => 
    ADDITIONAL_CATEGORIES.includes(cat.id) && userCategories.includes(cat.id));

  pickerOptions.innerHTML = `
    ${availableToAdd.length > 0 ? `
      <div class="picker-section"><h4>Add Topics</h4><div class="picker-grid">
        ${availableToAdd.map(cat => `
          <button class="picker-item" data-category="${cat.id}" data-action="add">
            <span class="picker-icon">${categoryIcons[cat.id] || '📰'}</span>
            <span class="picker-name">${cat.name}</span>
            <span class="picker-action">+ Add</span>
          </button>
        `).join('')}
      </div></div>
    ` : ''}
    ${addedCategories.length > 0 ? `
      <div class="picker-section"><h4>Your Added Topics</h4><div class="picker-grid">
        ${addedCategories.map(cat => `
          <button class="picker-item picker-item-added" data-category="${cat.id}" data-action="remove">
            <span class="picker-icon">${categoryIcons[cat.id] || '📰'}</span>
            <span class="picker-name">${cat.name}</span>
            <span class="picker-action">× Remove</span>
          </button>
        `).join('')}
      </div></div>
    ` : ''}
    <div class="picker-section suggest-section">
      <h4>Missing a topic?</h4>
      <div class="suggest-form">
        <input type="text" id="suggest-topic-input" placeholder="e.g. Crypto, Space, Gaming..." maxlength="50">
        <button class="btn btn-primary" id="suggest-topic-btn">Suggest</button>
      </div>
      <p class="suggest-hint">We review all suggestions and add popular ones!</p>
    </div>
  `;

  pickerOptions.querySelectorAll('.picker-item').forEach(item => {
    item.addEventListener('click', () => {
      const catId = item.dataset.category;
      item.dataset.action === 'add' ? addCategory(catId) : removeCategory(catId);
    });
  });

  const suggestBtn = document.getElementById('suggest-topic-btn');
  if (suggestBtn) suggestBtn.addEventListener('click', submitTopicSuggestion);
  const suggestInput = document.getElementById('suggest-topic-input');
  if (suggestInput) suggestInput.addEventListener('keypress', e => { if (e.key === 'Enter') submitTopicSuggestion(); });
}

function addCategory(categoryId) {
  if (!userCategories.includes(categoryId)) {
    setUserCategories([...userCategories, categoryId]);
    renderCategoryTabs();
    renderCategoryPicker();
    showToast(`Added ${categoryIcons[categoryId] || ''} ${getCategoryName(categoryId)}`);
  }
}

function removeCategory(categoryId) {
  const newCategories = userCategories.filter(id => id !== categoryId);
  setUserCategories(newCategories);
  if (currentCategory === categoryId) {
    setCurrentCategory(newCategories[0] || 'world');
    loadStories(currentCategory);
  }
  renderCategoryTabs();
  renderCategoryPicker();
  showToast(`Removed ${getCategoryName(categoryId)}`);
}

async function submitTopicSuggestion() {
  const input = document.getElementById('suggest-topic-input');
  const btn = document.getElementById('suggest-topic-btn');
  if (!input || !btn) return;

  const topic = input.value.trim();
  if (!topic) { showToast('Please enter a topic name'); return; }

  btn.disabled = true;
  btn.textContent = 'Sending...';

  try {
    const response = await fetch(`${API_BASE}/suggestions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ topic })
    });
    if (response.ok) { showToast('Thanks! We will review your suggestion.'); input.value = ''; }
    else { const data = await response.json(); showToast(data.error || 'Failed to submit suggestion'); }
  } catch (error) {
    console.error('Error submitting suggestion:', error);
    showToast('Failed to submit suggestion');
  } finally {
    btn.disabled = false;
    btn.textContent = 'Suggest';
  }
}

function getCategoryName(categoryId) {
  const cat = allCategories.find(c => c.id === categoryId);
  return cat ? cat.name : categoryId;
}
